import { Box, Stack, Theme, Typography } from '@mui/material';
import { ICertificateFullInfo, ITransferInfo, TransferStatus, TransferType } from '@repo/types';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { StatusBadge } from 'components/pages/certificates/StatusBadge';
import { TermInfo } from 'components/UI/TermInfo';
import { font } from 'settings/tokens';
import {
  stateCardSurfaceSx,
  stateCardTextSx,
} from 'pages/CertificatePage/components/stateCardSurface';
import { GiftInActionsSection } from './giftIn/GiftInActionsSection';
import { GiftInInfoSection } from './giftIn/GiftInInfoSection';
import { GiftOutInfoSection } from './giftOut/GiftOutInfoSection';
import { GiftOutActionsSection } from './giftOut/GiftOutActionsSection';
import { TransferInInfoSection } from './transferIn/TransferInInfoSection';
import { TransferInActionsSection } from './transferIn/TransferInActionsSection';

/**
 * Card heading per transfer direction — names the state the chip then qualifies.
 *
 * EXPORTED because `CertificatePage` needs the same three words for this card's
 * entry in the ranked state stack (`StateCardStack`), where a collapsed state is
 * announced by name. Re-typing them there would be the second hand-roll of one
 * string table (§36) and the two would eventually disagree about what a gift-out is
 * called.
 */
export const CARD_HEADING: Record<string, string> = {
  'gift-in': 'Gift received',
  'gift-out': 'Gift sent',
  'transfer-in': 'Transfer received',
};

/**
 * VERA-304 — the heading the EXPANDED card uses, which names the party as a preposition
 * because the party is rendered immediately beneath it.
 *
 * Two maps rather than one, and deliberately: `CARD_HEADING` is also the label for this
 * state's COLLAPSED row in `StateCardStack`, where nothing follows the words. "Gift
 * received from" ending a one-line announcement reads as a sentence that was cut off, so
 * the collapsed row keeps the noun phrase and only the card takes the preposition.
 *
 * A key with no entry here falls back to `CARD_HEADING`, so a new transfer direction is
 * never headingless.
 */
export const CARD_HEADING_WITH_PARTY: Record<string, string> = {
  'gift-in': 'Gift received from',
  'gift-out': 'Gift sent to',
  'transfer-in': 'Transfer received from',
};

/**
 * The glossary entry each heading's NOUN means (VRG-426).
 *
 * A parallel map rather than a lookup on the heading string, for the reason the
 * heading table itself is a table: "Gift received" and "Gift sent" are two headings
 * with one definition, and deriving the id from the words would either need a
 * `startsWith` (which breaks the day a heading is reworded) or a second copy of the
 * collapse. Same shape as `getCertificateStatusTermId`, and same contract: a key with
 * no entry simply has no `(i)`, because `TermInfo` renders nothing for an id it
 * cannot resolve.
 *
 * This card is the one place a GIFT is named to the person who did not choose it.
 * A gift-in reader has an item arriving from someone else and two live questions the
 * card's own rows do not answer — whether it is already theirs, and what accepting
 * does — which is precisely what the entry says ("the certificate moves to them once
 * they accept, and the handover joins the item's provenance"). It is also a
 * transient, single-instance surface, so the glyph is never repeated down a list.
 */
export const CARD_TERM_ID: Record<string, string> = {
  'gift-in': 'gift',
  'gift-out': 'gift',
  'transfer-in': 'transfer',
};

/**
 * VERA-304: whether the pending-transfer card is on screen, and for which direction.
 *
 * Exported because TWO components need the same answer and they must not disagree:
 * this section (which renders the card) and `CertificatePage` (which must suppress the
 * page-title status chip when the card is showing).
 *
 * VERA-304 — the count this used to police has changed and the old wording is no longer
 * true. It said "so the status appears exactly ONCE", which held while the card carried
 * its own chip. That chip is gone (the hero now shows only `AuthenticChip`, and the
 * evidence ladder lives in Provenance as plain text), so when the card is showing the
 * status appears ZERO times as a chip and is carried by the card's heading and copy
 * instead.
 *
 * The suppression is still required and the hook still earns its keep: duplicating the
 * condition across the two components would drift into a title chip that contradicts
 * the card beneath it. Only the arithmetic in the comment was stale.
 */
export const useTransferCardState = (certificate: ICertificateFullInfo | null) => {
  const { profile } = useSelector(({ auth }) => auth);
  const transfer = certificate?.transfer as (ITransferInfo & { code: string }) | undefined;

  //TODO PEDRO: useDisplayStatus for this when implemented
  const transferType = useMemo(() => {
    const profileInValidion =
      profile?.userId === transfer?.toCustomer?.id || profile?.id === transfer?.toCustomer?.id;
    const profileOutValidation =
      profile?.id === transfer?.fromCustomer?.id || profile?.userId === transfer?.fromCustomer?.id;

    if (profileInValidion && transfer?.type === TransferType.Primary) return 'transfer-in';

    if (profileInValidion && transfer?.type === TransferType.Gift) {
      return 'gift-in';
    }

    if (profileOutValidation && transfer?.type === TransferType.Gift) return 'gift-out';
  }, [transfer, profile]);
  //end TODO

  // A settled transfer (canceled / succeeded) is no longer pending, so the card retires.
  const visible = Boolean(
    certificate &&
      transfer &&
      transfer.status !== TransferStatus.Canceled &&
      transfer.status !== TransferStatus.Success &&
      transferType
  );

  return { visible, transferType };
};

export const TransferSummarySection = ({
  certificate,
}: {
  certificate: ICertificateFullInfo | null;
}) => {
  const [shouldShowOnCertificatePage, setShouldShowOnCertificatePage] = useState(true);

  const { transferType } = useTransferCardState(certificate);
  const transfer = certificate?.transfer as ITransferInfo & { code: string };

  /**
   * When this card gets an ENTRANCE.
   *
   * Two cases, and the second was added for VERA-304 at user request ("when a card
   * appears it should animate — slide down / pop in", pointing at the gift-received
   * card on the recipient's side).
   *
   * **1. It appeared because the user acted.** They sent a gift, the mutation
   * resolved, the card materialised. Always animates, in every direction.
   *
   * The trap here is the async load, and it is why this is not "was it here on the
   * previous render": `certificate` arrives NULL first, so the card is absent on the
   * first render of EVERY page load, and a naive previous-render check would animate
   * every visit. So the flag is only set once the certificate has actually LOADED and
   * genuinely has no card.
   *
   * **2. It is an INCOMING gift or transfer, present in the first loaded payload.**
   * Animates too, and the asymmetry with the outgoing card is the whole point.
   *
   * The original rule — never animate on load, because "animating it stages an event
   * that did not happen" — is right about the SENDER. They created the pending state
   * themselves and revisit the page while it is outstanding; replaying an entrance
   * each time would tell them the gift was just sent, repeatedly. It does not hold
   * for the RECIPIENT: they did not create this state, someone else did, and the
   * card is the news. The certificate arrives, then the card appears under the
   * photograph — an appearance, and the animation is what makes it read as one
   * rather than as a block that was always there.
   *
   * Latched through a ref so the flag cannot flip mid-mount and swap the emotion
   * class out from under a running animation.
   */
  const hasCard = Boolean(transferType);
  const cardWasAbsentWhileLoaded = useRef(false);
  if (certificate && !hasCard) cardWasAbsentWhileLoaded.current = true;

  const isIncoming = transferType === 'gift-in' || transferType === 'transfer-in';
  const incomingArrivedOnLoad = useRef<boolean | null>(null);
  if (incomingArrivedOnLoad.current === null && certificate) {
    incomingArrivedOnLoad.current = hasCard && isIncoming;
  }

  const animateIn =
    hasCard && (cardWasAbsentWhileLoaded.current || incomingArrivedOnLoad.current === true);


  /**
   * VERA-304: a pending transfer is an EPHEMERAL state — it exists only between
   * initiation and accept/cancel — and the app's convention is that temporary states
   * render in a card above the tabs (see `MissingStateCallout`, the Lost/Stolen
   * callout, which uses exactly these tokens). This block previously rendered bare
   * with only a top hairline, so the one genuinely transient state on the page was
   * the only one with no container.
   *
   * The card wraps the info row AND its actions together, because they are one unit:
   * "here's the pending state, here's what you can do about it." It also lives here
   * on the shared container rather than inside a single info section, so gift-in,
   * gift-out and transfer-in all get it without three copies.
   */
  const cardSx = useMemo(
    () =>
      shouldShowOnCertificatePage
        ? {
            /*
             * VERA-315 — the card's surface (fill, radius, elevation, no border) now
             * comes from ONE source shared with `MissingStateCallout`, which is where
             * the reasoning lives. Both files used to say "these two must stay
             * identical" and then each set their own; they had drifted.
             *
             * The measured defect it fixes: the card background and the page
             * background were both `#FFFFFF`, i.e. 1.00:1 — no surface at all.
             */
            ...stateCardSurfaceSx,
            ...stateCardTextSx,
            /*
              A short rise + fade, NOT a page-level transition (those travel a
              full 100% of the element's own size). This is a block appearing
              inside a page that is already still, so it only needs enough
              movement to read as "this arrived" — a longer travel reads as a
              panel sliding in and pulls focus off the page around it.

              It borrows the app's existing motion vocabulary rather than inventing
              a curve: 260ms / `cubic-bezier(0.32, 0.72, 0, 1)` are the page-slide
              keyframes' own numbers (`utils/pageTransition`), so a card arriving
              and a page pushing decelerate identically. Only the DISTANCE differs,
              for the reason above.

              ⚠️ `opacity` and `transform` ONLY — never height, margin or padding.
              A transform is composited and takes no part in layout, so the product
              details, tabs and everything else below the card hold still while it
              arrives. Animating height here would make the entire page below it
              jump for 260ms, which is worse than no animation at all.

              `both` + a one-shot keyframe: Emotion caches the class, so a re-render
              cannot replay it.
            */
            ...(animateIn
              ? {
                  '@keyframes veraStateCardIn': {
                    from: { opacity: 0, transform: 'translate3d(0, 8px, 0)' },
                    to: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
                  },
                  animation: 'veraStateCardIn 260ms cubic-bezier(0.32, 0.72, 0, 1) both',
                  /*
                    Motion-sensitive users get the END state with no travel, which is
                    what `none` produces here because the keyframe animates FROM the
                    hidden state and the element's own rest state is visible. Written
                    the other way round — resting hidden, animating INTO view — a
                    suppressed animation would leave the card permanently invisible.
                    Same rule `utils/pageTransition` and `SuccessState` follow.
                  */
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }
              : null),
          }
        : // Nothing visible renders in this state (the info row is hidden and the actions
          // sections are drawers only), so an empty bordered card would be a stray box.
          undefined,
    [shouldShowOnCertificatePage, animateIn]
  );

  useEffect(() => {
    if (!certificate || !transfer) return setShouldShowOnCertificatePage(false);

    if (
      transfer?.status === TransferStatus.Canceled ||
      transfer?.status === TransferStatus.Success
    ) {
      return setShouldShowOnCertificatePage(false);
    }
    return setShouldShowOnCertificatePage(true);
  }, [certificate, transfer]);

  return (
    <Box sx={cardSx}>
      {/*
        Heading only — the status chip is GONE from this row.

        It said "Transfer" next to a heading already reading "Gift sent": the same
        category, in a different word, in the most colour-saturated element of the
        card, which pulled the eye to the least informative thing in it. VERA-304
        put it here so the chip appeared exactly once rather than twice (page
        title AND card), and that constraint still holds — it is suppressed at the
        title via `useTransferCardState`. Once is not the same as necessary, and
        the heading is the clearer of the two.
      */}
      {shouldShowOnCertificatePage && (
        <Stack direction='row' alignItems='center' spacing={0.5} sx={{ mb: 1.5 }}>
          {/* `font.body` (16) / 500 — REVERTED from the `font.heading` (20/600) this
              briefly carried, on the same day, at user request: the card heading is
              to be in parity with the claim-ownership screen's own title.

              What that parity IS, concretely: `SuccessState` — the confirmation the
              Claim ownership button leads to — titles itself `font.body` / 500
              (`components/UI/SuccessState.tsx`), and `ScanTag`'s success screen uses
              16/500 too. So one flow was announcing itself at 20/600 on the card and
              16/500 two taps later, which is the drift; 16/500 is the value the rest
              of the flow already agrees on and the value these cards held before.

              The 20/600 bump was argued as "at 16 the heading matched the sender's
              name beneath it, so nothing said which was the heading." That is a real
              observation with a cheaper fix than growing the heading: the name below
              renders at 16/400 through `UserPreview`, so 500 vs 400 already separates
              them, and it is the same separation every other card in this family uses.

              Applied to ALL FOUR cards sharing `stateCardSurfaceSx`
              (`MissingStateCallout`, `SuspiciousActivityCard`, `VerifyPossessionCard`)
              — changing one alone is what would turn a family into four unrelated
              boxes, in either direction. */}
          <Typography sx={{ fontSize: font.body, fontWeight: 500 }}>
            {CARD_HEADING_WITH_PARTY[transferType ?? ''] ??
              CARD_HEADING[transferType ?? ''] ??
              'Transfer pending'}
          </Typography>
          {/* Only the CARD gets the `(i)`. `CARD_HEADING` is exported because
              `CertificatePage` reuses the same words for this state's COLLAPSED row in
              `StateCardStack`; `CARD_TERM_ID` is deliberately not used there, where the
              glyph would sit on a one-line announcement of a state the reader has
              chosen not to expand — an offer to explain something not on screen. */}
          <TermInfo termId={CARD_TERM_ID[transferType ?? ''] ?? ''} />
        </Stack>
      )}
      {shouldShowOnCertificatePage && (
        <InfoSection transferType={transferType} certificate={certificate} />
      )}
      {/*
        20px above the action, against 12px between the heading and the info rows.

        The gap is deliberately the largest inside the card because it is the one
        real boundary here: everything above it DESCRIBES the state, and the button
        ACTS on it. At the internal 12px the button read as one more row of the
        explanation. Set on the shared container so gift-out, gift-in and
        transfer-in cannot drift to three different values.
      */}
      <Box sx={{ mt: shouldShowOnCertificatePage ? 2.5 : 0 }}>
        <ActionsSection
          transferType={transferType}
          shouldShowOnCertificatePage={shouldShowOnCertificatePage}
          certificate={certificate}
        />
      </Box>
    </Box>
  );
};

const InfoSection = ({
  transferType,
  certificate,
}: {
  transferType?: string;
  certificate: ICertificateFullInfo | null;
}) => {
  if (!certificate) return null;

  switch (transferType) {
    case 'gift-in':
      return <GiftInInfoSection certificate={certificate} />;
    case 'gift-out':
      return <GiftOutInfoSection certificate={certificate} />;
    case 'transfer-in':
      return <TransferInInfoSection certificate={certificate} />;
    default:
      return null;
  }
};

const ActionsSection = ({
  transferType,
  shouldShowOnCertificatePage,
  certificate,
}: {
  transferType?: string;
  shouldShowOnCertificatePage: boolean;
  certificate: ICertificateFullInfo | null;
}) => {
  if (!certificate) return null;

  switch (transferType) {
    case 'gift-in':
      return (
        <GiftInActionsSection
          certificate={certificate}
          shouldShowOnCertificatePage={shouldShowOnCertificatePage}
        />
      );
    case 'gift-out':
      return (
        <GiftOutActionsSection
          certificate={certificate}
          shouldShowOnCertificatePage={shouldShowOnCertificatePage}
        />
      );
    case 'transfer-in':
      return (
        <TransferInActionsSection
          certificate={certificate}
          shouldShowOnCertificatePage={shouldShowOnCertificatePage}
        />
      );
    default:
      return null;
  }
};
